import React from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";

const LAST_UPDATED = "June 2026";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-bone-100">
      <LegalHeader />
      <main className="max-w-3xl mx-auto px-6 py-16 md:py-24" data-testid="privacy-page">
        <div className="border border-clay-600/40 bg-clay-400/10 text-clay-600 text-sm px-4 py-3 mb-10">
          <b>Draft, pending legal review.</b> This page is a plain-language draft and has not yet been reviewed by a lawyer.
        </div>

        <div className="overline mb-3">Privacy</div>
        <h1 className="font-heading text-4xl md:text-5xl text-forest-700">Privacy Policy</h1>
        <p className="text-sm text-forest-400 mt-3">Last updated: {LAST_UPDATED}</p>
        <p className="text-forest-500 mt-6 leading-relaxed">
          Toothpix is operated by <b>Zahar Lab</b> ("we", "us"). This policy explains, in plain
          language, what we do with the photos and details patients share when they use a smile
          preview. If you're an orthodontist evaluating Toothpix, this is written to answer the
          questions your patients will ask you.
        </p>

        <Section title="What we collect">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>The <b>photo</b> a patient uploads to generate their smile preview.</li>
            <li>The <b>AI-generated "after" image</b> we create from that photo.</li>
            <li>The patient's <b>name</b> and <b>email address</b>.</li>
            <li>Optionally, the patient's <b>phone number</b>, if they choose to provide it.</li>
          </ul>
        </Section>

        <Section title="Where photos are stored, and for how long">
          <p>
            Photos and generated images are stored on secure, access-controlled cloud object
            storage. They are retained for as long as the clinic that owns the lead keeps its
            account active, so the clinic can refer back to the result during a consultation. A
            patient can ask for their photos to be deleted at any time (see below), and we will
            remove them promptly.
          </p>
        </Section>

        <Section title="We never train external AI models on patient photos">
          <p>
            Patient photos are used <b>only</b> to generate that patient's own smile preview. We do
            <b> not</b> use them to train, fine-tune, or improve any external or third-party AI
            model, and we do not sell or share them for advertising.
          </p>
        </Section>

        <Section title="Who can see the photos">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>The <b>clinic</b> that owns the lead, the orthodontist the patient chose to try.</li>
            <li><b>Zahar Lab</b>, as the operator of the platform, for support and to keep the service running.</li>
          </ul>
          <p className="mt-3">
            That's it. Photos are not visible to other clinics or to the public.
          </p>
        </Section>

        <Section title="How a patient can request deletion">
          <p>
            A patient can request that their photo, generated image, and contact details be deleted
            by emailing{" "}
            <a href="mailto:privacy@toothpix.ai" className="text-forest-600 underline underline-offset-4">
              privacy@toothpix.ai
            </a>
            . We will confirm and complete the deletion promptly.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions about this policy? Email{" "}
            <a href="mailto:hello@toothpix.ai" className="text-forest-600 underline underline-offset-4">
              hello@toothpix.ai
            </a>{" "}
            (Zahar Lab).
          </p>
        </Section>
      </main>
      <LegalFooter />
    </div>
  );
}

function LegalHeader() {
  return (
    <header className="border-b border-bone-200 bg-white">
      <div className="max-w-3xl mx-auto px-6 h-16 flex items-center">
        <Link to="/" className="flex items-center gap-2" data-testid="legal-home-link">
          <Sparkles size={20} strokeWidth={1.5} className="text-forest-600" />
          <span className="font-heading text-2xl text-forest-700">Toothpix</span>
        </Link>
      </div>
    </header>
  );
}

function LegalFooter() {
  return (
    <footer className="border-t border-bone-200 py-10 px-6">
      <div className="max-w-3xl mx-auto flex flex-col md:flex-row justify-between gap-4 text-xs text-forest-400">
        <div>© {new Date().getFullYear()} Toothpix. All rights reserved. · A Zahar Lab product</div>
        <div className="flex gap-6">
          <Link to="/privacy" className="hover:text-forest-700">Privacy</Link>
          <Link to="/terms" className="hover:text-forest-700">Terms</Link>
        </div>
      </div>
    </footer>
  );
}

export function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-heading text-2xl text-forest-700">{title}</h2>
      <div className="text-forest-500 mt-3 leading-relaxed">{children}</div>
    </section>
  );
}
